import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, Paperclip, X } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import DataTable from "../../components/admin/DataTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import ScheduleEditor from "../../components/admin/ScheduleEditor";
import Header from "../../components/Header";
import scheduledEmailsService from "../../api/services/scheduledEmails.service";
import { toEmailHtml } from "../../utils/emailHtml";
import {
  describeSchedule,
  scheduleError,
  scheduleFromServer,
  scheduleToPatch,
} from "./emailSchedule";

const STATUS_STYLES = {
  scheduled: { label: "Scheduled", chip: "bg-blue-100 text-blue-800" },
  paused: { label: "Paused", chip: "bg-amber-100 text-amber-800" },
  completed: { label: "Finished", chip: "bg-green-100 text-green-800" },
  cancelled: { label: "Cancelled", chip: "bg-gray-100 text-gray-700" },
};

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Finished" },
  { value: "cancelled", label: "Cancelled" },
];

// Attachments and inline images are fixed once scheduled, so the editor
// here has no image button; change those by cancelling and rescheduling.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const formatEastern = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        timeZone: "America/New_York",
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const buttonStyle =
  "px-3 py-1.5 rounded-lg border text-xs font-manrope font-medium transition-colors";

/** Change a scheduled email's subject, message and timing. */
function EditDialog({ item, onClose, onSaved }) {
  const [subject, setSubject] = useState(item.subject);
  // Emails scheduled before message_source existed only have the HTML.
  const [source, setSource] = useState(item.message_source ?? item.message);
  const [schedule, setSchedule] = useState(() => scheduleFromServer(item));
  const [saving, setSaving] = useState(false);
  const problem = scheduleError(schedule);
  const blank = !source || !source.replace(/<[^>]*>/g, "").trim();

  const save = async () => {
    setSaving(true);
    try {
      const saved = await scheduledEmailsService.update(item.id, {
        subject: subject.trim(),
        message: toEmailHtml(source),
        message_source: source,
        ...scheduleToPatch(schedule),
      });
      toast.success("Scheduled email updated");
      onSaved(saved);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not save the changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-scheduled-title"
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 id="edit-scheduled-title" className="text-lg font-semibold font-manrope text-text-primary">
            Edit scheduled email
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm font-manrope text-text-muted">
          Goes to: <strong className="text-text-primary">{item.recipient_label}</strong>
          {item.attachment_names?.length ? (
            <span className="block mt-1">
              <Paperclip className="inline w-3 h-3 mr-1" />
              {item.attachment_names.join(", ")}
            </span>
          ) : null}
        </p>

        <div>
          <label htmlFor="edit-subject" className="text-sm font-medium text-gray-700 font-manrope">
            Subject
          </label>
          <input
            id="edit-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-manrope"
          />
        </div>

        <div>
          <span className="text-sm font-medium text-gray-700 font-manrope">Message</span>
          <ReactQuill theme="snow" value={source} onChange={setSource} modules={QUILL_MODULES} />
        </div>

        <ScheduleEditor value={schedule} onChange={setSchedule} />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-manrope"
          >
            Close
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !subject.trim() || blank || Boolean(problem)}
            className="px-4 py-2 rounded-lg bg-[#F3BC48] text-[#173151] font-semibold text-sm font-manrope disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ScheduledEmails() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await scheduledEmailsService.list({ status: statusFilter });
      setItems(data?.items || []);
    } catch {
      toast.error("Could not load scheduled emails");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const replace = (saved) =>
    setItems((prev) => prev.map((i) => (i.id === saved.id ? saved : i)));

  const act = async (action, item, message) => {
    try {
      replace(await scheduledEmailsService[action](item.id));
      toast.success(message);
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "That didn't work; try again");
    }
  };

  const columns = [
    {
      key: "subject",
      label: "Email",
      render: (value, row) => (
        <div className="min-w-[180px]">
          <p className="font-medium text-text-primary font-manrope">{value}</p>
          <p className="text-xs text-text-muted font-manrope">{row.recipient_label}</p>
          {row.attachment_names?.length ? (
            <p className="text-xs text-text-muted font-manrope">
              <Paperclip className="inline w-3 h-3 mr-1" />
              {row.attachment_names.length} attachment(s)
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: "repeat",
      label: "Schedule",
      render: (_, row) => (
        <span className="text-sm font-manrope">
          {describeSchedule(scheduleFromServer(row))}
        </span>
      ),
    },
    {
      key: "next_run_at",
      label: "Next send (Eastern)",
      render: (value) => <span className="text-sm font-manrope">{formatEastern(value)}</span>,
    },
    {
      key: "run_count",
      label: "Sent so far",
      render: (value, row) => (
        <div className="text-sm font-manrope">
          <span>{value} time(s)</span>
          {row.last_run_at ? (
            <p className="text-xs text-text-muted">
              Last: {formatEastern(row.last_run_at)}
              {row.last_sent_count != null ? `, ${row.last_sent_count} delivered` : ""}
              {row.last_failed_count ? `, ${row.last_failed_count} failed` : ""}
            </p>
          ) : null}
          {row.last_error ? <p className="text-xs text-red-600">{row.last_error}</p> : null}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (value) => {
        const style = STATUS_STYLES[value] || { label: value, chip: "bg-gray-100 text-gray-700" };
        return (
          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${style.chip}`}>
            {style.label}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "",
      render: (_, row) => {
        const open = row.status === "scheduled" || row.status === "paused";
        if (!open) return null;
        return (
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={() => setEditing(row)}
              className={`${buttonStyle} border-gray-300 hover:border-[#173151]`}
            >
              Edit
            </button>
            {row.status === "scheduled" ? (
              <button
                type="button"
                onClick={() => act("pause", row, "Paused")}
                className={`${buttonStyle} border-gray-300 hover:border-[#173151]`}
              >
                Pause
              </button>
            ) : (
              <button
                type="button"
                onClick={() => act("resume", row, "Resumed")}
                className={`${buttonStyle} border-gray-300 hover:border-[#173151]`}
              >
                Resume
              </button>
            )}
            <button
              type="button"
              onClick={() => setCancelling(row)}
              aria-label="Cancel email"
              className={`${buttonStyle} border-red-200 text-red-700 hover:bg-red-50`}
            >
              Cancel
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header />

      <div className="max-w-9xl mx-auto px-3 sm:px-4 py-4 flex-1 flex flex-col min-h-0 w-full">
        <div className="shrink-0 mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl md:text-[30px] lg:text-[46px] font-bold text-text-primary font-kollektif truncate">
            Scheduled Emails
          </h1>
          <p className="text-xs sm:text-sm text-neutral-main font-manrope mt-1 hidden sm:block">
            Mass emails waiting to go out, once or on repeat. Schedule a new
            one from Mass Email.
          </p>
        </div>

        <div className="shrink-0 flex flex-wrap items-center gap-2 mb-4">
          <CalendarClock className="w-4 h-4 text-gray-400" />
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setStatusFilter(option.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-manrope font-medium border transition-colors ${
                statusFilter === option.value
                  ? "bg-[#173151] text-white border-[#173151]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-[#173151]"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-auto">
          <DataTable
            columns={columns}
            data={items}
            loading={loading}
            emptyMessage="No scheduled emails."
          />
        </div>
      </div>

      {editing && (
        <EditDialog
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            replace(saved);
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(cancelling)}
        onClose={() => setCancelling(null)}
        onConfirm={async () => {
          const item = cancelling;
          setCancelling(null);
          await act("cancel", item, "Scheduled email cancelled");
        }}
        title="Cancel scheduled email"
        message={
          cancelling
            ? `Cancel "${cancelling.subject}"? It will not be sent again. This can't be undone.`
            : ""
        }
        variant="danger"
      />
    </div>
  );
}
